import { animated, useSpring } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import React, { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";

interface IProps {
  children: any;
  link: string;
  isSaving?: boolean;
  setCroppedBlob: (blob: any) => void;
  setIsSaving: (bool: boolean) => void;
  videoRange: { startTime: number; endTime: number };
}

const Crop = ({
  children,
  link,
  isSaving,
  setCroppedBlob,
  setIsSaving,
  videoRange
}: IProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const DragRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const outputCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const [{ x, y, width, height }, api] = useSpring(() => ({
    x: 0,
    y: 0,
    width: 72 * 2.3,
    height: 128 * 2.3
  }));

  const bind = useDrag(
    (state) => {
      const isResizing = state.event.target === DragRef.current;

      (window as any).movement = state.movement;
      (window as any).offset = state.offset;

      if (isResizing) {
        api.set({ width: state.offset[0], height: state.offset[1] });
      } else {
        api.set({ x: state.offset[0], y: state.offset[1] });
      }
    },
    {
      from: (event) => {
        const isResizing = event.target === DragRef.current;
        if (isResizing) {
          return [width.get(), height.get()];
        } else {
          return [x.get(), y.get()];
        }
      },
      bounds: (state) => {
        const isResizing = state?.event.target === DragRef.current;
        const containerWidth = containerRef.current?.clientWidth || 0;
        const containerHeight = containerRef.current?.clientHeight || 0;

        if (isResizing) {
          return {
            top: 50,
            left: 50,
            right: containerWidth - x.get(),
            bottom: containerHeight - y.get()
          };
        } else {
          return {
            top: 0,
            left: 0,
            right: containerWidth - width.get(),
            bottom: containerHeight - height.get()
          };
        }
      }
    }
  );

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const { startTime, endTime } = videoRange;

    const loopHandler = () => {
      if (video.currentTime >= endTime) {
        video.currentTime = startTime;
        video.play();
      }
    };

    video.addEventListener("timeupdate", loopHandler);

    return () => {
      video.removeEventListener("timeupdate", loopHandler);
    };
  }, [videoRange]);

  const startRecording = useCallback(() => {
    const video = videoRef.current;
    const canvas = outputCanvasRef.current;
    const container = containerRef.current;

    if (!video || !canvas || !container) return;

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    const displayedWidth = container.clientWidth;
    const displayedHeight = container.clientHeight;

    const scaleX = videoWidth / displayedWidth;
    const scaleY = videoHeight / displayedHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const OUTPUT_WIDTH = 720;
    const OUTPUT_HEIGHT = 1280;

    canvas.width = OUTPUT_WIDTH;
    canvas.height = OUTPUT_HEIGHT;

    const draw = () => {
      const cropX = x.get() * scaleX;
      const cropY = y.get() * scaleY;
      const cropW = width.get() * scaleX;
      const cropH = height.get() * scaleY;

      ctx.clearRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
      ctx.drawImage(
        video,
        cropX,
        cropY,
        cropW,
        cropH,
        0,
        0,
        OUTPUT_WIDTH,
        OUTPUT_HEIGHT
      );
      requestAnimationFrame(draw);
    };

    const handleStart = () => {
      draw();

      const stream = canvas.captureStream(30);
      const mediaRecorder = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];

      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });

        setCroppedBlob(blob);
        setIsSaving(false);
      };

      mediaRecorder.start();

      setTimeout(
        () => mediaRecorder.stop(),
        (videoRange.endTime - videoRange.startTime) * 1000
      );
    };

    if (video.readyState >= 3) {
      video.play();
      handleStart();
    } else {
      video.oncanplay = () => {
        video.play();
        handleStart();
      };
    }
  }, []);

  useEffect(() => {
    if (isSaving) {
      startRecording();
    }
  }, [isSaving, startRecording]);

  return (
    <Container ref={containerRef}>
      <div style={{ position: "relative" }}>
        <HiddenVideo
          ref={videoRef}
          src={link}
          autoPlay
          loop
          muted
          playsInline
          crossOrigin="anonymous"
        />
        <canvas
          ref={outputCanvasRef}
          style={{
            position: "absolute",
            visibility: "hidden"
          }}
        />
        <CroppedArea style={{ x, y, width, height }} {...bind()}>
          <Resizer ref={DragRef} />
        </CroppedArea>
        {children}
      </div>
    </Container>
  );
};

export default Crop;

const Container = styled.div`
  position: relative;
  overflow: visible;
  min-width: 300px;
  min-height: 300px;

  button {
    position: absolute;
    bottom: -10%;
    z-index: 500;
  }
`;

const HiddenVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 37rem;
  height: 37rem;
  object-fit: cover;
  /* visibility: hidden; */
`;

const CroppedArea = styled(animated.div)`
  border: 1px dashed ${Colors.primary};
  position: absolute;
  top: 0px;
  left: 0px;

  width: 60%;
  height: 35rem;
  z-index: 100;
  box-shadow: 0 0 2000px 2000px rgb(0 0 0 / 30%);
  touch-action: none;
  cursor: move;
`;

const Resizer = styled.div`
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 10px;
  height: 10px;
  background-color: ${Colors.primary};
  cursor: nwse-resize;
  touch-action: none;
`;
